import React, { useEffect, useState, useContext, useCallback } from "react";

import Input from "shared/components/FormElements/Input";
import CustomSelect from "shared/components/FormElements/Select";
import DatePicker from "shared/components/FormElements/DatePicker";
import NotFound from "shared/components/UIElements/NotFound";
import DeleteModal from "components/shared/DeleteModal";
import ItemGenerated from "components/shared/ItemGenerated";
import FormFooter from "shared/components/FormElements/FormFooter";
import { useTranslation } from "react-i18next";

// #region Utils
import { VALIDATOR_REQUIRE, VALIDATOR_NONE } from "shared/util/validators";
import { paymentMethods, banks, isOfficial, paymentTypes, expenseTypes } from "shared/util/types";
// #endregion Utils

// #region Hooks
import { useForm } from "shared/hooks/form-hook";
import { useHttpClient } from "shared/hooks/http-hook";
import { StoreContext } from "shared/context/store-context";
// #endregion Hooks

const ExpenseItem = ({ itemId, onClose, isNewItem, gotoItem, DeleteItem, UpdateItem, InsertItem }) => {
  const { t } = useTranslation();
  //standard code
  const [isItemLoaded, setIsItemLoaded] = useState(false);
  const [loadedItem, setLoadedItem] = useState();
  const [generatedItemId, setGeneratedItemId] = useState();
  const { isLoading, error, sendRequest } = useHttpClient();
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const itemType = "expense";
  const endPointUpdate = "expenses";
  const endPointDelete = "expenses";
  const endPointCreate = "expenses";

  const { expenses, schoolOptions } = useContext(StoreContext);

  const [formState, inputHandler] = useForm(
    {
      school: { value: "", isValid: true },
      date: { value: "", isValid: true },
      type: { value: "", isValid: true },
      description: { value: "", isValid: true },
      vehicle: { value: "", isValid: true },
      person: { value: "", isValid: true },
      paymentMethod: { value: "", isValid: true },
      paymentType: { value: "", isValid: true },
      amount: { value: null, isValid: true },
      bank: { value: null, isValid: true },
      receiptNo: { value: null, isValid: true },
      official: { value: null, isValid: true },
    },
    true
  );

  const fetchItem = useCallback(async () => {
    setIsItemLoaded(false);
    try {
      const retrievedItem = expenses.find((item) => item.id === itemId);
      setLoadedItem(retrievedItem);
    } catch (err) {}
    setIsItemLoaded(true);
  }, [expenses, itemId]);

  useEffect(() => {
    fetchItem();
  }, [itemId, fetchItem]);

  const itemUpdateSubmitHandler = async (event) => {
    event.preventDefault();

    const endpoint = isNewItem
      ? `${process.env.REACT_APP_BACKEND_URL}/${endPointCreate}`
      : `${process.env.REACT_APP_BACKEND_URL}/${endPointUpdate}/${itemId}`;

    const postType = isNewItem ? "POST" : "PATCH";

    const infoToBeSent = {
      school: formState.inputs.school.value,
      date: formState.inputs.date.value,
      type: formState.inputs.type.value,
      description: formState.inputs.description.value,
      vehicle: formState.inputs.vehicle.value,
      person: formState.inputs.person.value,
      paymentMethod: formState.inputs.paymentMethod.value,
      paymentType: formState.inputs.paymentType.value,
      amount: formState.inputs.amount.value,
      bank: formState.inputs.bank.value,
      receiptNo: formState.inputs.receiptNo.value,
      official: formState.inputs.official.value,
    };

    try {
      const responseData = await sendRequest(endpoint, postType, JSON.stringify(infoToBeSent));

      if (responseData?.[itemType]) {
        if (isNewItem) {
          InsertItem(responseData[itemType]);
          setGeneratedItemId(responseData[itemType].id);
        } else {
          UpdateItem(responseData[itemType]);
          window.toast.success(t("updateSuccess"));
        }
      }
    } catch (err) {
      if (err?.message) {
        window.toast.error(err.message);
      } else {
        window.toast.error(t("actionFailed"));
      }
    }
  };

  if (isItemLoaded && !isNewItem && !loadedItem && !error) {
    return <NotFound item={itemType}></NotFound>;
  }

  if (generatedItemId) {
    return (
      <ItemGenerated
        itemType={itemType}
        onGotoNewItem={() => gotoItem(generatedItemId)}
        onCreateNewItem={() => setGeneratedItemId(null)}
        onClose={() => {
          setGeneratedItemId(false);
          onClose();
        }}
      />
    );
  }

  return (
    <React.Fragment>
      <DeleteModal
        endPoint={endPointDelete}
        itemType={itemType}
        itemId={itemId}
        onDeleted={() => {
          DeleteItem(itemId);
          onClose();
        }}
        onClose={() => setShowConfirmModal(false)}
        showModal={showConfirmModal}
      />

      {isItemLoaded && (
        <form k className="form-container" onSubmit={itemUpdateSubmitHandler}>
          <CustomSelect
            options={schoolOptions}
            id="school"
            onInput={inputHandler}
            label="Okul *"
            initialValue={
              schoolOptions.find((option) => option?.value?.toString() === loadedItem?.school?.toString()) || null
            }
            validators={[VALIDATOR_REQUIRE()]}
            initialValid={true}
          ></CustomSelect>

          <DatePicker
            id="date"
            onInput={inputHandler}
            label="Fatura Tarihi"
            initialValid={true}
            errorText="Gider tarihini giriniz."
            validators={[VALIDATOR_REQUIRE()]}
            initialValue={loadedItem?.date?.toString() || null}
          ></DatePicker>

          <CustomSelect
            options={expenseTypes}
            id="type"
            onInput={inputHandler}
            label="Gider Türü"
            errorText="Gider türünü giriniz."
            initialValid={true}
            validators={[VALIDATOR_REQUIRE()]}
            initialValue={
              expenseTypes.find((expense) => expense?.value?.toString() === loadedItem?.type?.toString()) || null
            }
          ></CustomSelect>

          <Input
            id="description"
            element="input"
            type="text"
            label="Açıklama"
            validators={[VALIDATOR_NONE()]}
            errorText="Açıklama giriniz."
            initialValue={loadedItem?.description || ""}
            initialValid={true}
            onInput={inputHandler}
          />

          <Input
            id="person"
            element="input"
            type="text"
            label="Kurum-Kişi Adı"
            validators={[VALIDATOR_NONE()]}
            initialValue={loadedItem?.person || ""}
            initialValid={true}
            onInput={inputHandler}
          />

          <Input
            id="vehicle"
            element="input"
            type="text"
            label="Araç plakası (Araca yönelik bir ödeme ise)"
            validators={[VALIDATOR_NONE()]}
            initialValue={loadedItem?.vehicle || ""}
            initialValid={true}
            onInput={inputHandler}
          />

          <CustomSelect
            options={paymentMethods}
            id="paymentMethod"
            onInput={inputHandler}
            label="Ödeme Yöntemi"
            validators={[VALIDATOR_REQUIRE()]}
            initialValid={true}
            initialValue={
              paymentMethods?.find(
                (paymentMethod) => paymentMethod?.value?.toString() === loadedItem?.paymentMethod?.toString()
              ) || paymentMethods[0]
            }
          ></CustomSelect>

          <CustomSelect
            options={paymentTypes}
            id="paymentType"
            onInput={inputHandler}
            label="Ödeme Şekli"
            validators={[VALIDATOR_REQUIRE()]}
            initialValid={true}
            initialValue={
              paymentTypes?.find(
                (paymentType) => paymentType?.value?.toString() === loadedItem?.paymentType?.toString()
              ) || paymentTypes[0]
            }
          ></CustomSelect>

          <CustomSelect
            options={banks}
            id="bank"
            onInput={inputHandler}
            label="Ödemeyi hangi banka hesabınızdan yaptınız. (Banka ödemesi ise banka ismi seçiniz)"
            validators={[VALIDATOR_NONE()]}
            initialValid={true}
            initialValue={banks?.find((bank) => bank?.value?.toString() === loadedItem?.bank?.toString()) || banks[0]}
          ></CustomSelect>

          <Input
            id="receiptNo"
            element="input"
            type="text"
            label="Fatura Numarası"
            validators={[VALIDATOR_NONE()]}
            errorText="Gider karşılık alınan faturasını giriniz."
            initialValue={loadedItem?.receiptNo || ""}
            initialValid={true}
            onInput={inputHandler}
          />

          <CustomSelect
            options={isOfficial}
            id="official"
            onInput={inputHandler}
            label="Resmi bir ödeme mi?"
            initialValid={true}
            validators={[VALIDATOR_REQUIRE()]}
            initialValue={
              isOfficial?.find((item) => item?.value?.toString() === loadedItem?.official?.toString()) || isOfficial[1]
            }
          ></CustomSelect>

          <Input
            id="amount"
            element="input"
            type="number"
            label="Ödeme Tutarı *"
            validators={[VALIDATOR_REQUIRE()]}
            errorText="Ödeme tutarını giriniz."
            initialValue={loadedItem?.amount || ""}
            initialValid={isNewItem ? false : true}
            onInput={inputHandler}
          />

          <FormFooter
            isNewItem={isNewItem}
            onUpdate={itemUpdateSubmitHandler}
            onDelete={() => setShowConfirmModal(true)}
            isUpdating={isLoading}
            disabled={
              !formState.isValid ||
              isLoading ||
              !formState.inputs.type.value ||
              !formState.inputs.school.value ||
              !formState.inputs.date.value ||
              !formState.inputs.amount.value
            }
          />
        </form>
      )}
    </React.Fragment>
  );
};

export default ExpenseItem;
